import {
	getCardMapInfoEstratos,
	getCardMapInfoFazendas,
	getCardMapInfoFazendasPoligonos,
	getCardMapInfoFazendasPoligonosByFazendaId,
	getPoligonosByInventarioIdList,
	getPlantiosMapByFazendaId,
} from '../../services/requests/DashBoardRequests'

import chroma from 'chroma-js'
import { variaveisBiometricas } from '../../constants/Mappings/VariaveisBiometricasDashboard'

import * as turf from '@turf/turf'
import axios from 'axios'
import _, { maxBy, minBy, uniqBy } from 'lodash'
import { CoordenatesToGooglePattern } from '../../utils/MapFunctions'
import { colapsarFaixaIdade } from '../../utils/helpers'
import i18n from '../../VueI18n'

/**
 * Replica `DateTime.RecuperarIdadeEmAnos()` do backend (anos completos) e
 * complementa com o saldo de meses para o PlantioMarker. Os `anos` aqui são
 * idênticos ao valor que compõe `idadeDosPlantios`/`faixaDeIdadeDosPlantios`
 * no card, garantindo consistência entre pino e card.
 */
function recuperarIdadeAnosMeses(data) {
	const hoje = new Date()
	const dataPlantio = new Date(data)
	let anos = hoje.getFullYear() - dataPlantio.getFullYear()
	let meses = hoje.getMonth() - dataPlantio.getMonth()
	if (hoje.getDate() < dataPlantio.getDate()) {
		meses--
	}
	if (meses < 0) {
		anos--
		meses += 12
	}
	return { anos, meses }
}

/**
 * Módulo que gerencia o estado das mensagens exibidas na aplicacao
 * @state Estados que devem ser controlados
 * @mutations Funções que tem permissão para alterar o state
 * @actions Executar operações asincronas e chamar mutations
 * @getters Retorna o estado além da possibilidade de retornar a
 * mesma lógica do computed (Gera valores com base em valores originais)
 */

const state = {
	showMarkers: false,
	variavelBiometricaMapa: 'volumeTotalComCasca',
	plantiosFazenda: {},
	/**
	 * Source cancel token para as requisicoes do dashboard card info
	 */
	sourceCancelToken: {},

	/**
	 * Dados relacionados a informacoes gerais das fazendas ou de uma fazenda especifica
	 */
	cardInfo: [],

	/**
	 * Dados relacionados a geo (poligono, centroid, etc...)
	 */
	infoGeo: [],

	/**
	 * Caso esteja buscando os dados do Card do backend
	 */
	isLoadingCardMap: true,

	/**
	 * Caso esteja buscando os dados de infoGeo
	 */
	isLoadingPolygons: true,
}

const mutations = {
	/**
	 * Limpa todos os estados para o valor inicial
	 */
	cleanStateBeforeRequest: (state) => {
		state.cardInfo = []
		state.plantiosFazenda = {}
		state.isLoadingCardMap = true
		state.isLoadingPolygons = true
	},

	/**
	 * Seta o valor para o bounds
	 */
	setBounds: (state, bounds) => {
		const [west, south, east, north] = bounds
		state.bounds = { east, north, south, west }
	},

	/**
	 * Cancela uma requisicao caso ela exista e eeta o novo cancel token
	 */
	setSourceCancelToken: (state) => {
		if (Object.keys(state.sourceCancelToken).length > 0) {
			state.sourceCancelToken.cancel('Requisicao para card cancelada')
		}
		state.sourceCancelToken = axios.CancelToken.source()
	},

	/**
	 * Seta o valor para o CardInfo
	 */
	setCardInfo: (state, cardInfo) => {
		cardInfo.forEach((f) => {
			f.infoGeo = {}
			f.isActived = false
		})
		state.cardInfo = cardInfo
		state.isLoadingCardMap = false
	},

	/**
	 * Marca ou desmarca todos os cardInfo de uma vez (selecionar/limpar selecao)
	 */
	setAllCardInfoActived: (state, isActived) => {
		state.cardInfo.forEach((f) => {
			f.isActived = isActived
		})
	},

	setPlantiosFazenda: (state, plantiosFazenda) => {
		state.plantiosFazenda = plantiosFazenda
	},

	setShowMarkers: (state, value) => {
		state.showMarkers = value
	},

	/**
	 * Seta o estado de infoGeo
	 */
	setInfoGeo: (state, infoGeo) => {
		state.infoGeo = infoGeo
		state.isLoadingPolygons = false
	},

	/**
	 * Faz o merge entre os poligonos e o cardinfo
	 */
	mergeInfoGeoIntoCardInfo: (state) => {
		if (state.cardInfo.length > 0 && state.infoGeo.length > 0) {
			state.cardInfo.forEach((cardInfo) => {
				const infoGeoFounded = state.infoGeo.find((infoGeo) => infoGeo.id === cardInfo.id)
				if (infoGeoFounded) {
					infoGeoFounded.paths = CoordenatesToGooglePattern(
						infoGeoFounded.poligono.coordinates,
						infoGeoFounded.poligono.type,
					)
					cardInfo.infoGeo = infoGeoFounded
				}
			})
			state.infoGeo = []
		}
	},
}

const actions = {
	/**
	 * Recupera os plantios dos inventários selecionados para dispor no mapa
	 */

	getPlantiosEstratosSelecionados({ commit, state }) {
		state.showMarkers = false
		const inventarios = state.cardInfo
			.filter((f) => f.isActived && f.inventarioEstratoFlorestalId != null)
			.map((m) => m.inventarioEstratoFlorestalId)

		if (inventarios.length) {
			getPoligonosByInventarioIdList(inventarios).then((response) => {
				commit(
					'setPlantiosFazenda',
					turf.featureCollection(uniqBy(response.data, (o) => o.properties['Codigo Plantio'])),
				)
			})
		}
	},

	/**
	 * Recupera os dados para o CardInfo, da visão geral de todas as fazendas
	 */
	apiCardInfoGeral({ commit, state }) {
		commit('cleanStateBeforeRequest')
		commit('setSourceCancelToken')

		getCardMapInfoFazendas(state.sourceCancelToken.token).then((response) => {
			const result = response.data
			const cardMapInfoFazenda = result.map((cardMapInfoFazenda) => {
				const areaDaFazenda = cardMapInfoFazenda.areaDaFazenda
					? `${cardMapInfoFazenda.areaDaFazenda} (ha)`
					: '-'
				const faixaDeIdadeDosPlantios = cardMapInfoFazenda.faixaDeIdadeDosPlantios
					? `${colapsarFaixaIdade(cardMapInfoFazenda.faixaDeIdadeDosPlantios)} ${i18n.t('anos')}`
					: '-'
				const volumeTotalSemCasca = cardMapInfoFazenda.volumeTotalSemCasca
					? `${cardMapInfoFazenda.volumeTotalSemCasca} (m³/ha)`
					: '-'
				const taxaDeMortalidade = cardMapInfoFazenda.taxaDeMortalidade
					? `${Number(cardMapInfoFazenda.taxaDeMortalidade).toFixed(2).replace('.', ',')} (%)`
					: '-'

				return {
					id: cardMapInfoFazenda.fazendaId,
					titulo: cardMapInfoFazenda.nomeFazenda,
					existeProcessamento: cardMapInfoFazenda.existeProcessamento,
					infos: [
						{ label: i18n.t('area'), value: areaDaFazenda },
						{ label: i18n.t('intervaloDeIdade'), value: faixaDeIdadeDosPlantios },
						{ label: i18n.t('totalVolumeWithOutBark'), value: volumeTotalSemCasca },
						{ label: i18n.t('mortalityRate'), value: taxaDeMortalidade },
					],
				}
			})
			commit('setCardInfo', cardMapInfoFazenda)
			commit('mergeInfoGeoIntoCardInfo')
		})

		getCardMapInfoFazendasPoligonos(state.sourceCancelToken.token).then((response) => {
			const result = response.data
			commit('setInfoGeo', result.poligonos)
			commit('setBounds', result.bounds)
			commit('mergeInfoGeoIntoCardInfo')
		})
	},

	/**
	 * Recupera os dados para o CardInfo, de uma fazenda especifica com informacoes dos estratos processados
	 */
	apiCardInfoFazenda({ commit, state }, fazendaId) {
		commit('cleanStateBeforeRequest')
		commit('setSourceCancelToken')

		getCardMapInfoEstratos(fazendaId, state.sourceCancelToken.token).then((response) => {
			const result = response.data
			const cardMapInfoEstrato = result.map((cardMapInfoEstrato) => {
				const areaDoEstrato = cardMapInfoEstrato.areaDoEstrato
					? `${cardMapInfoEstrato.areaDoEstrato} (ha)`
					: '-'
				const numeroDePlantios = cardMapInfoEstrato.numeroDePlantios ?? '-'
				const idadeDosPlantios = cardMapInfoEstrato.idadeDosPlantios
					? `${colapsarFaixaIdade(cardMapInfoEstrato.idadeDosPlantios)} ${i18n.t('anos')}`
					: '-'
				const volumeTotalSemCasca = cardMapInfoEstrato.volumeTotalSemCasca
					? `${cardMapInfoEstrato.volumeTotalSemCasca} (m³/ha)`
					: '-'
				const incrementoMedioAnual = cardMapInfoEstrato.incrementoMedioAnual
					? `${cardMapInfoEstrato.incrementoMedioAnual} (m³/ha/ano)`
					: '-'
				const taxaDeMortalidade = cardMapInfoEstrato.taxaDeMortalidade
					? `${Number(cardMapInfoEstrato.taxaDeMortalidade).toFixed(2).replace('.', ',')} (%)`
					: '-'

				return {
					id: cardMapInfoEstrato.estratoFlorestalId,
					inventarioEstratoFlorestalId: cardMapInfoEstrato.inventarioEstratoFlorestalId,
					titulo: cardMapInfoEstrato.descricaoEstratoFlorestal,
					infos: [
						{ label: i18n.t('area'), value: areaDoEstrato },
						{ label: i18n.t('plantationsNumber'), value: numeroDePlantios },
						{ label: i18n.t('intervaloDeIdade'), value: idadeDosPlantios },
						{ label: i18n.t('totalVolumeWithOutBark'), value: volumeTotalSemCasca },
						{ label: i18n.t('aai'), value: incrementoMedioAnual },
						{ label: i18n.t('mortalityRate'), value: taxaDeMortalidade },
					],
				}
			})
			commit('setCardInfo', cardMapInfoEstrato)
			commit('mergeInfoGeoIntoCardInfo')

			if (state.cardInfo[0] && state.cardInfo[0].inventarioEstratoFlorestalId != null) {
				getPoligonosByInventarioIdList([state.cardInfo[0].inventarioEstratoFlorestalId]).then(
					(response) => {
						commit(
							'setPlantiosFazenda',
							turf.featureCollection(uniqBy(response.data, (o) => o.properties['Codigo Plantio'])),
						)
						// console.log("🚀 ~ file: moduleDashboardCardInfo.js:225 ~ getPoligonosByInventarioIdList ~ response:", response)
						// state.cardInfo[0].isActived = true;
						// dispatch("apiMarkersPlantio", state.cardInfo[0].inventarioEstratoFlorestalId)
					},
				)
			} else {
				getPlantiosMapByFazendaId(fazendaId, state.sourceCancelToken.token).then((response) => {
					commit('setPlantiosFazenda', JSON.parse(response.data.message))
				})
			}
		})

		getCardMapInfoFazendasPoligonosByFazendaId(fazendaId, state.sourceCancelToken.token).then(
			(response) => {
				const result = response.data
				commit('setInfoGeo', result.poligonos)
				commit('setBounds', result.bounds)
				commit('mergeInfoGeoIntoCardInfo')
			},
		)
	},
}

const getters = {
	/**
	 *
	 * @param {*} state
	 * @returns retorna todos os card infos selecionados
	 */
	cardInfosActivated: (state) => {
		return state.cardInfo.filter((f) => f.isActived)
	},
	/**
	 * Retorna os cardInfo que contenham informacoes de geo
	 */
	cardInfoWithInfoGeo: (state) => {
		return state.cardInfo.filter((f) => !_.isEmpty(f.infoGeo))
	},

	/**
	 * Filtra as informacoes de acordo com o textFilter digitado
	 */
	cardInfoFiltredByTitleAndInfos: (state) => (textFilter) => {
		return state.cardInfo.filter((cardInfo) => {
			if (cardInfo.titulo?.toLowerCase().includes(textFilter.toLowerCase())) {
				return true
			}

			return cardInfo.infos
				.filter((f) => f.value !== null && f.value !== undefined)
				.map((m) => m.value)
				.some((s) => s?.toString().toLowerCase().includes(textFilter.toLowerCase()))
		})
	},

	plantioMarkers: (state) => {
		return state.plantiosFazenda.features.map((plantio) => {
			const { anos, meses } = recuperarIdadeAnosMeses(plantio.properties['Data de Plantio'])
			return {
				area: plantio.properties.AreaEfetiva,
				areaDoTalhao: plantio.properties.AreaDoTalhao,
				codigo: plantio.properties['Codigo Plantio'],
				dataDoProcessamento: new Date().toISOString(), //TODO altear quando propriedade estiver disponível
				idEstratoInventarioFlorestal: 'f7724509-3cbf-4276-8e01-b74bab372b1b', //TODO varificar necessidade
				idade: anos,
				idadeMeses: meses,
				incrementoMedioAnual: plantio.properties.imaComCasca,
				infoPoligono: { ...plantio.properties.infoPoligono, poligono: plantio },
				materialGenetico: plantio.properties['Material Genetico'],
				porcentagemDeAreaPlantadaNoTalhao: plantio.properties.porcentagemDeAreaPlantadaNoTalhao,
				porcentagemDeArvoresUteis: plantio.properties.proporcaoArvoresUteis,
				volumeTotal: plantio.properties.volumeTotalComCasca,
			}
		})
	},

	plantiosCodigosUnicos: (state) => {
		return uniqBy(state.plantiosFazenda.features, (o) => o.properties['Codigo Plantio'])
	},

	coresPlantios: (state) => {
		const talhoes = Object.values(state.plantiosFazenda).length
			? state.plantiosFazenda.features
			: []

		let min = minBy(talhoes, (o) => o.properties[state.variavelBiometricaMapa]).properties[
			state.variavelBiometricaMapa
		]
		let max = maxBy(talhoes, (o) => o.properties[state.variavelBiometricaMapa]).properties[
			state.variavelBiometricaMapa
		]

		// Adiciona uma pequena margem aos valores mínimos e máximos
		min -= 0.01 * (max - min)
		max += 0.01 * (max - min)

		const mediaNormalizada = (media) => ((media - min) / (max - min)) * (1 - 0) + 0

		// const talhoes = this.valoresVariavelBiometrica
		const variavel = variaveisBiometricas.find((f) => f.key === state.variavelBiometricaMapa).label

		const cores = chroma.scale(['#385622', '#a6d08e', '#f9db69', '#fe0201'].reverse())
		//.colors(talhoes.length)

		return talhoes
			.map(({ properties }) => {
				return {
					codigo: properties['Codigo Plantio'],
					variavel,
					media: properties[state.variavelBiometricaMapa],
				}
			})
			.map((talhao) => {
				return {
					...talhao,
					cor: cores(mediaNormalizada(talhao.media) || 0.5).hex(),
				}
			})
	},

	inventariosIdSelecionados: (state) => {
		return state.cardInfo
			.filter((f) => f.isActived && f.inventarioEstratoFlorestalId != null)
			.map((m) => m.inventarioEstratoFlorestalId)
	},

	bounds: (state) => {
		if (Object.values(state.plantiosFazenda).length) {
			const [west, north, east, south] = turf.bbox(state.plantiosFazenda)
			return { east, west, south, north }
		}

		return {}
	},

	maxByVariavelBiometrica: ({ plantiosFazenda, variavelBiometricaMapa }) => {
		if (plantiosFazenda.features) {
			const { properties } = maxBy(
				plantiosFazenda.features,
				(o) => o.properties[variavelBiometricaMapa],
			)
			return properties[variavelBiometricaMapa]
		}

		return 0
	},
	minByVariavelBiometrica: ({ plantiosFazenda, variavelBiometricaMapa }) => {
		if (plantiosFazenda.features) {
			const { properties } = minBy(
				plantiosFazenda.features,
				(o) => o.properties[variavelBiometricaMapa],
			)
			return properties[variavelBiometricaMapa]
		}

		return 0
	},
}

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
}
